<template>
  <div>
    <h1 class="title mqu-subtitle">Additional Outgoing Fees and Features</h1>
    <h2 class="subtitle">These questions relate to additional fees / features. You may need to use industry average figures for these items. If you don’t know what values to use then choose <strong>No</strong> or <strong>Default</strong>. (The contract may not fully specify all these items.)</h2>
    <rv-tri-radio name="has-outgoing-delay" label="Will there be a delay in receiving your exit payment after leaving the village?" v-model="data.outgoingDelayOption" option-a="Yes" option-b="No" option-c="Default" tooltip="There may be a delay between when you leave the RV and when you
receive your exit payout (refund less fees). Delays of 6 months or longer are not uncommon.">
      <span slot="option-a">
        Yes
      </span>
      <span slot="option-b">
        No
      </span>
      <span slot="option-c">
        Default
      </span>
    </rv-tri-radio>
    <transition name="fade" mode="out-in">
      <div v-if="data.outgoingDelayOption !== 'No'">
        <outgoing-delay v-model="data.outgoingDelay" v-on:input="update" :disabled="data.outgoingDelayOption === 'Default'"/>
      </div>
    </transition>
    <rv-tri-radio name="has-outgoing-refurbish-fee" label="Will you have to pay for refurbishing the apartment?" v-model="data.outgoingRefurbishFeeOption" option-a="Yes" option-b="No" option-c="Default" tooltip="The contract may require you to pay for refurbishing the unit (e.g. bathroom
/ kitchen) to make it easier to sell to a new resident.">
      <span slot="option-a">
        Yes
      </span>
      <span slot="option-b">
        No
      </span>
      <span slot="option-c">
        Default
      </span>
    </rv-tri-radio>
    <transition name="fade" mode="in-out">
      <div v-if="data.outgoingRefurbishFeeOption === 'Yes'">
        <outgoing-refurbish-fee v-model="data.outgoingRefurbishFee" v-on:input="update"/>
      </div>
      <div v-if="data.outgoingRefurbishFeeOption === 'Default'">
        <outgoing-refurbish-fee v-model="data.outgoingRefurbishFee" v-on:input="update" disabled="true"/>
      </div>
    </transition>
    <rv-tri-radio name="has-operator-commission" label="Are you charged a commission when your apartment is re-sold?" v-model="data.operatorCommissionOption" option-a="Yes" option-b="No" option-c="Default" tooltip="This may be payable to the rv operator or a real estate agent and is a
percentage (e.g. 2% - 3%) of the new resident’s entry fee.">
      <span slot="option-a">
        Yes
      </span>
      <span slot="option-b">
        No
      </span>
      <span slot="option-c">
        Default
      </span>
    </rv-tri-radio>
    <transition name="fade" mode="out-in">
      <div v-if="data.operatorCommissionOption === 'Yes'">
        <operator-commission v-model="data.operatorCommission" v-on:input="update"/>
      </div>
      <div v-if="data.operatorCommissionOption === 'Default'">
        <operator-commission v-model="data.operatorCommission" disabled="true"/>
      </div>
    </transition>
    <rv-tri-radio name="has-capital-maintenance-fee" label="Is there a Capital Maintenance fee?" v-model="data.capitalMaintenanceFeeOption" option-a="Yes" option-b="No" option-c="Default" tooltip="Typically this applies to the entry fee, eg. 0.3% per year of residence,
capped at 10 years">
      <span slot="option-a">
        Yes
      </span>
      <span slot="option-b">
        No
      </span>
      <span slot="option-c">
        Default
      </span>
    </rv-tri-radio>
    <transition name="fade" mode="in-out">
      <div v-if="data.capitalMaintenanceFeeOption === 'No'">
        <div v-show="false">
          <capital-maintenance-fee v-model="data.capitalMaintenanceFee" v-on:input="update"/>
          <capital-maintenance-period v-model="data.capitalMaintenancePeriod" v-on:input="update"/>
        </div>
      </div>
      <div v-if="data.capitalMaintenanceFeeOption === 'Yes'">
        <capital-maintenance-fee v-model="data.capitalMaintenanceFee" v-on:input="update"/>
        <capital-maintenance-period v-model="data.capitalMaintenancePeriod" v-on:input="update"/>
      </div>
      <div v-if="data.capitalMaintenanceFeeOption === 'Default'">
        <capital-maintenance-fee v-model="data.capitalMaintenanceFee" v-on:input="update" disabled="true"/>
        <capital-maintenance-period v-model="data.capitalMaintenancePeriod" v-on:input="update" disabled="true"/>
      </div>
    </transition>
  </div>
</template>

<script>
import OutgoingDelay from '../inputs/additional-outgoing-fees/OutgoingDelay'
import OutgoingRefurbishFee from '../inputs/additional-outgoing-fees/OutgoingRefurbishFee'
import OperatorCommission from '../inputs/additional-outgoing-fees/OperatorCommission'
import CapitalMaintenanceFee from '../inputs/additional-outgoing-fees/CapitalMaintenanceFee'
import CapitalMaintenancePeriod from '../inputs/additional-outgoing-fees/CapitalMaintenancePeriod'
import RvTriRadio from '../inputs/RvTriRadio'

export default {
  props: ['value'],
  inject: ['$validator'],
  components: {
    RvTriRadio,
    CapitalMaintenancePeriod,
    CapitalMaintenanceFee,
    OperatorCommission,
    OutgoingRefurbishFee,
    OutgoingDelay
  },
  data: function() {
    return {
      fired: false,
      data: {
        outgoingDelayOption: this.value.outgoingDelayOption,
        outgoingRefurbishFeeOption: this.value.outgoingRefurbishFeeOption,
        operatorCommissionOption: this.value.operatorCommissionOption,
        capitalMaintenanceFeeOption: this.value.capitalMaintenanceFeeOption,
        outgoingDelay: this.value.outgoingDelay,
        outgoingRefurbishFee: this.value.outgoingRefurbishFee,
        operatorCommission: this.value.operatorCommission,
        capitalMaintenanceFee: this.value.capitalMaintenanceFee,
        capitalMaintenancePeriod: this.value.capitalMaintenancePeriod
      },
      defaultData: {
        outgoingDelayOption: 'Default',
        outgoingRefurbishFeeOption: 'Default',
        operatorCommissionOption: 'Default',
        capitalMaintenanceFeeOption: 'Default',
        outgoingDelay: '6',
        outgoingRefurbishFee: '15000',
        operatorCommission: '3',
        capitalMaintenanceFee: '0.2',
        capitalMaintenancePeriod: '10'
      }
    }
  },
  methods: {
    update: function() {
      this.$emit('input', this.data)
    }
  },
  watch: {
    'data.outgoingDelayOption': function(val) {
      if (this.fired === false) {
        if (val === 'Default') {
          this.data.outgoingDelayOption = 'No'
          this.fired = true
          this.$nextTick(function() {
            this.data.outgoingDelay = this.defaultData.outgoingDelay
            this.data.outgoingDelayOption = 'Default'
            this.$nextTick(function() {
              this.fired = false
            })
          })
        } else if (val === 'No') {
          this.data.outgoingDelay = '0'
        }
        this.update()
      }
    },
    'data.outgoingRefurbishFeeOption': function(val) {
      if (this.fired === false) {
        if (val === 'Default') {
          this.data.outgoingRefurbishFeeOption = 'No'
          this.fired = true
          this.$nextTick(function() {
            this.data.outgoingRefurbishFee = this.defaultData.outgoingRefurbishFee
            this.data.outgoingRefurbishFeeOption = 'Default'
            this.$nextTick(function() {
              this.fired = false
            })
          })
        } else if (this.data.outgoingRefurbishFeeOption === 'No') {
          this.data.outgoingRefurbishFee = '0'
        }
        this.update()
      }
    },
    'data.operatorCommissionOption': function(val) {
      if (this.fired === false) {
        if (val === 'Default') {
          this.data.operatorCommissionOption = 'No'
          this.fired = true
          this.$nextTick(function() {
            this.data.operatorCommission = this.defaultData.operatorCommission
            this.data.operatorCommissionOption = 'Default'
            this.$nextTick(function() {
              this.fired = false
            })
          })
        } else if (val === 'No') {
          this.data.operatorCommission = '0'
        }
        this.update()
      }
    },
    'data.capitalMaintenanceFeeOption': function(val) {
      if (this.fired === false) {
        if (val === 'Default') {
          this.data.capitalMaintenanceFeeOption = 'No'
          this.fired = true
          this.$nextTick(function() {
            this.data.capitalMaintenanceFee = this.defaultData.capitalMaintenanceFee
            this.data.capitalMaintenancePeriod = this.defaultData.capitalMaintenancePeriod
            this.data.capitalMaintenanceFeeOption = 'Default'
            this.$nextTick(function() {
              this.fired = false
            })
          })
        } else if (val === 'No') {
          this.data.capitalMaintenanceFee = '0'
          this.data.capitalMaintenancePeriod = '0'
        }
        this.update()
      }
    }
  }
}
</script>

<style scoped>
.mqu-title {
  font-family: 'National-Extrabold';
}

.mqu-subtitle {
  font-family: 'National-Light';
}
</style>



// WEBPACK FOOTER //
// src/components/modules/stages/AdditionalOutgoingFees.vue